import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonBase } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPage } from 'services/imports/fileToPages';

const useStyles = makeStyles((theme: Theme) => createStyles({
  slot: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0.75),
  },
  button: {
    cursor: 'zoom-in',
    '&:hover $thumb': {
      borderColor: theme.palette.primary.main,
    },
  },
  focusVisible: {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
  thumb: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(7),
    height: theme.spacing(7),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  warningIcon: {
    color: theme.palette.error.dark,
    fontSize: theme.typography.h6.fontSize,
  },
}));

type PageThumbProps = {
  page: ImportPage;
  onOpen?: () => void;
};

/** A page thumbnail; with `onOpen` it is a button that opens the full preview. */
export const PageThumb = ({ page, onOpen }: PageThumbProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const names = { file: page.fileName, page: page.pageNumber };

  const image = (
    <span className={classes.thumb}>
      {page.failed ? (
        <FontAwesomeIcon icon={faExclamationTriangle} className={classes.warningIcon} aria-hidden="true" />
      ) : (
        <img src={page.imageDataUrl} alt={t('imports:pages.thumbnailAlt', names)} className={classes.image} />
      )}
    </span>
  );

  if (!onOpen) return <span className={classes.slot}>{image}</span>;
  const openLabel: string = t('imports:pages.openPreview', names);

  return (
    <ButtonBase
      aria-label={openLabel}
      focusVisibleClassName={classes.focusVisible}
      className={`${classes.slot} ${classes.button}`}
      onClick={onOpen}
    >
      {image}
    </ButtonBase>
  );
};
